/**
 * schedulingService — TV-RV-11
 *
 * Front-desk scheduling surface: thin client over sandman's
 * /api/v1/scheduling endpoints. All appointment state is owned by
 * sandman (which writes FHIR + PG); this module never calls FHIR or
 * Firestore directly.
 *
 * Base URL: VITE_SANDMAN_URL (same var used by useTelehealthMetering)
 * Auth:     Bearer <KC access token> from AuthProvider.token
 *
 * Sandman scheduling API (from collabkarebusiness appointmentService.js):
 *   GET  /api/v1/scheduling/availability?providerId=&from=&to=
 *   POST /api/v1/scheduling/book
 *   GET  /api/v1/scheduling/appointments?patientId=&date=
 *   PATCH /api/v1/scheduling/appointments/:id          (reschedule)
 *   PATCH /api/v1/scheduling/appointments/:id/cancel   (cancel)
 */

const SANDMAN_URL =
  import.meta.env.VITE_SANDMAN_URL || 'https://sandman.collabkare.io';

/**
 * Shared fetch helper — attaches Authorization header, parses JSON,
 * throws a structured error on non-2xx.
 */
async function apiFetch(path, token, options = {}) {
  const resp = await fetch(`${SANDMAN_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });

  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    const err = new Error(`Sandman ${options.method || 'GET'} ${path} → ${resp.status} ${resp.statusText}: ${text}`);
    err.status = resp.status;
    throw err;
  }

  // 204 No Content
  if (resp.status === 204) return null;
  return resp.json();
}

/**
 * GET /api/v1/scheduling/availability
 *
 * Returns available slots for a provider in a date range.
 *
 * @param {string} token   - KC access token
 * @param {object} opts
 * @param {string} opts.providerId  - FHIR Practitioner reference or sandman provider ID
 * @param {string} opts.from        - ISO-8601 start date (e.g. "2026-06-21")
 * @param {string} opts.to          - ISO-8601 end date   (e.g. "2026-06-28")
 * @returns {Promise<{slots: Array}>}
 */
export async function getProviderAvailability(token, { providerId, from, to }) {
  const qs = new URLSearchParams({ providerId, from, to }).toString();
  return apiFetch(`/api/v1/scheduling/availability?${qs}`, token);
}

/**
 * POST /api/v1/scheduling/book
 *
 * Books an appointment and returns the created appointment including
 * the patient join link.
 *
 * @param {string} token - KC access token
 * @param {object} booking
 * @param {string} booking.providerId
 * @param {string} booking.patientId
 * @param {string} booking.slotId      - slot ID from getProviderAvailability
 * @param {string} booking.start       - ISO-8601 datetime
 * @param {string} booking.end         - ISO-8601 datetime
 * @param {string} [booking.chiefComplaint]
 * @param {string} [booking.tenantId]
 * @returns {Promise<{appointmentId, patientJoinLink, providerJoinLink, ...}>}
 */
export async function bookAppointment(token, booking) {
  return apiFetch('/api/v1/scheduling/book', token, {
    method: 'POST',
    body: JSON.stringify(booking),
  });
}

/**
 * GET /api/v1/scheduling/appointments
 *
 * Lists appointments with optional filters (date, patientId, providerId).
 *
 * @param {string} token   - KC access token
 * @param {object} [filters]
 * @param {string} [filters.date]       - "YYYY-MM-DD"
 * @param {string} [filters.patientId]
 * @param {string} [filters.providerId]
 * @returns {Promise<{appointments: Array}>}
 */
export async function getAppointments(token, filters = {}) {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(filters).filter(([, v]) => v != null)),
  ).toString();
  return apiFetch(`/api/v1/scheduling/appointments${qs ? `?${qs}` : ''}`, token);
}

/**
 * PATCH /api/v1/scheduling/appointments/:id
 *
 * Reschedules an existing appointment (updates start/end/slotId).
 *
 * @param {string} token          - KC access token
 * @param {string} appointmentId
 * @param {object} changes        - { start, end, slotId?, reason? }
 * @returns {Promise<object>}     - updated appointment
 */
export async function rescheduleAppointment(token, appointmentId, changes) {
  return apiFetch(`/api/v1/scheduling/appointments/${appointmentId}`, token, {
    method: 'PATCH',
    body: JSON.stringify(changes),
  });
}

/**
 * PATCH /api/v1/scheduling/appointments/:id/cancel
 *
 * Cancels an appointment.
 *
 * @param {string} token          - KC access token
 * @param {string} appointmentId
 * @param {string} [reason]       - cancellation reason text
 * @returns {Promise<object>}     - { cancelled: true, appointmentId }
 */
export async function cancelAppointment(token, appointmentId, reason) {
  return apiFetch(`/api/v1/scheduling/appointments/${appointmentId}/cancel`, token, {
    method: 'PATCH',
    body: JSON.stringify({ reason: reason || '' }),
  });
}
